import React, { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/global.css';
import stylesGlobal from '../../styles/referral/global.module.css';
import styles from './subscription.module.css';
import TVStickyButton from '../../components/TVStickyButton';
import { HeroVideo, TrialOffer, PremiumBenefits, UserReviewSlider, Review } from '../../components/pwatv';
import { NAVIGATION_ROUTES } from '../../constant';
import { useTokenStore } from '../../storage/tokenState';
import { _isEmpty } from '../../utils/referralLodash';
// import videoUrl from '../../assets/images/verticaltv/want-non-stop-drama-subscription.mp4';
import posterUrl from '../../assets/images/verticaltv/Screenshot-2025.png';
import { CLEVERTAP_EVENTS } from '../../analytics/trackerEvents/cleverTap/events';
import { trackCleverTapEvent } from '../../utils/cleverTapHelper';
import { CT_PARAMS } from '../../analytics/trackerEvents/cleverTap/parameters';
import { useReviewSync } from '../../hooks/useReviewSync';
import { FbPixelEvent_ViewContent } from '../../utils/FbPixel/fbPixel';
import { getUTMParamsFromURL, extractAndStoreTitleId, extractAndStoreUTMParams } from '../../utils/utmTracker';
import avatarPlaceholder from '../../assets/images/verticaltv/suraj.png';
import priyaimg from '../../assets/images/verticaltv/priya.png';
import ajayimg from '../../assets/images/verticaltv/ajay.png';
import lalitimg from '../../assets/images/verticaltv/lalit.png';
import appsFlyerSmartScriptLib from '../../utils/SmartScriptAppFlyer/appsFlyerSmartScriptLib';
import { fireContentViewEvent } from '../../utils/SmartScriptAppFlyer/appsFlyerEvents';
import { pushGAEvent, GA_EVENTS } from '../../utils/gaHelper';

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token: userToken } = useTokenStore();
  const eventTrackedRef = useRef(false);
  const smartScriptInitializedRef = useRef(false);
  const videoUrl = 'https://static-perf1.zupee.com/blog-images/uploads/2025/12/live_action_hb.mp4';
  // Memoize reviews array to prevent recreation on every render
  // This ensures images are only loaded once and reused
  const reviews: Review[] = useMemo(() => [
    {
      id: 1,
      name: 'Priya Singh',
      location: 'Jodhpur',
      rating: 5,
      text: 'Ab khaali time boring nahi lagta. Ek episode dekha, phir toh ruka hi nahi gaya. This is my best entertainment app!',
      avatar: priyaimg,
    },
    {
      id: 2,
      name: 'Suraj Sharma',
      location: 'Shimla',
      rating: 4,       
      text: 'This is my favourite app. Now my 10-minute tea break is the best part of my day. So much drama in so little time.',
      avatar: avatarPlaceholder,
       },
    {
      id: 3,
      name: 'Ajay Kulkarni',
      location: 'Mumbai',
      rating: 4,
      text: 'Best app for quick entertainment. The stories feel so real and the acting is top-notch. Totally worth subscribing.',
      avatar: ajayimg,
    },
    {
      id: 4,
      name: 'Lalit yadav',
      location: 'Gorakhpur',
      rating: 4,
      text: 'Love, romance, suspense - this app has every type of drama and story. I never get bored now.',
      avatar: lalitimg,
    },
  ], []); // Empty dependency array - reviews never change

  const handleButtonClick = () => {
    // Track Facebook Pixel ViewContent event when user clicks Start FREE trial
    FbPixelEvent_ViewContent();
    
    // Fire AppsFlyer ContentView event on button click
    fireContentViewEvent({ contentType: 'subscription', contentName: 'start_free_trial' });
    
    // Track GA event for Start Free Trial button click
    pushGAEvent(GA_EVENTS.START_FREE_TRIAL_CLICK);
    
    // Check if token exists and is valid (not expired)
    const hasAccessToken = !_isEmpty(userToken?.accessToken);
    const hasRefreshToken = !_isEmpty(userToken?.refreshToken);
    const currentTime = Date.now();
    
    // Check if access token is expired
    const tokenValidity = userToken?.validity || 0;
    const refreshTokenExpiresAt = userToken?.refreshTokenExpiresAt || 0;
    const isAccessTokenExpired = tokenValidity > 0 && currentTime > tokenValidity;
    const isRefreshTokenExpired = refreshTokenExpiresAt > 0 && currentTime > refreshTokenExpiresAt;
    
    // If user has tokens but they're expired, treat as logged out
    const isTokenValid = hasAccessToken && !isAccessTokenExpired && 
                        (hasRefreshToken && !isRefreshTokenExpired || !hasRefreshToken);
    
    if (isTokenValid) {
      // User is logged in with valid token - navigate to subscription payment
      navigate(NAVIGATION_ROUTES.REFERRAL_SUBSCRIPTION_PAYMENT);
    } else {
      // User is not logged in or token is expired - go to signup
      navigate(NAVIGATION_ROUTES.REFERRAL_SIGNUP);
    }
  };

  // Memoize the contextual text template to prevent recreation on every render
  const contextualTextTemplate = useMemo(
    () => (review: Review) => `${review.name} from ${review.location} took FREE trial 🎉`,
    []
  );

  // Use the review sync hook to manage review state and contextual text
  const { handleReviewChange, getContextualText } = useReviewSync({
    reviews,
    contextualTextTemplate,
  });

  // Memoize the contextual text to prevent recalculation on every render
  const contextualText = useMemo(() => getContextualText(), [getContextualText]);

  //Track trial screen view event when user lands on subscription page
  useEffect(() => {
    // Prevent duplicate event firing (handles React.StrictMode double-mounting)
    if (eventTrackedRef.current) {
      return;
    }
    
    // Extract and store titleId from URL (for Vertical TV redirect on Subscribed page)
    extractAndStoreTitleId();
    
    // Extract and store UTM parameters from URL (for Kafka events on subsequent pages)
    extractAndStoreUTMParams();
    
    // Extract UTM parameters from URL (when coming from third-party URLs with UTM params)
    const utmParams = getUTMParamsFromURL();
    
    // Check if user came from any external social media platform
    const redirectionPlatform = searchParams.get(CT_PARAMS.REDIRECTION_PLATFORM);
    const hasRedirectionParams = !!redirectionPlatform;
    
    // Build event data with UTM parameters and redirection parameters
    const eventData: Record<string, any> = {};
    
    // Add UTM parameters if they exist
    if (utmParams.utm_source) eventData[CT_PARAMS.UTM_SOURCE] = utmParams.utm_source;
    if (utmParams.utm_medium) eventData[CT_PARAMS.UTM_MEDIUM] = utmParams.utm_medium;
    if (utmParams.utm_campaign) eventData[CT_PARAMS.UTM_CAMPAIGN] = utmParams.utm_campaign;
    if (utmParams.utm_adset) eventData[CT_PARAMS.UTM_ADSET] = utmParams.utm_adset;
    if (utmParams.utm_ad) eventData[CT_PARAMS.UTM_AD] = utmParams.utm_ad;
    
    if (hasRedirectionParams) {
      // Extract redirection parameters from URL
      const redirectionUrl = searchParams.get(CT_PARAMS.REDIRECTION_URL);
      const redirectionPromoId = searchParams.get(CT_PARAMS.REDIRECTION_PROMO_ID);
      const seriesIdName = searchParams.get(CT_PARAMS.SERIES_ID_NAME);
      const promoType = searchParams.get(CT_PARAMS.PROMO_TYPE);
      
      if (redirectionUrl) eventData[CT_PARAMS.REDIRECTION_URL] = redirectionUrl;
      if (redirectionPlatform) eventData[CT_PARAMS.REDIRECTION_PLATFORM] = redirectionPlatform;
      if (redirectionPromoId) eventData[CT_PARAMS.REDIRECTION_PROMO_ID] = redirectionPromoId;
      if (seriesIdName) eventData[CT_PARAMS.SERIES_ID_NAME] = seriesIdName;
      if (promoType) eventData[CT_PARAMS.PROMO_TYPE] = promoType;
    }
    
    // Mark event as tracked to prevent duplicate firing
    eventTrackedRef.current = true;
    
    // Track event - always fire, with or without UTM/redirection parameters
    // GAID will be automatically added by trackCleverTapEvent
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_TRial_SCREEN_VIEW, Object.keys(eventData).length > 0 ? eventData : {});
    
  }, [searchParams]);

  // Initialize AppsFlyer SmartScript on page load
  // This captures UTM parameters from the URL, generates OneLink URL, and stores it in sessionStorage
  useEffect(() => {
    // Prevent duplicate initialization (handles React.StrictMode double-mounting)
    if (smartScriptInitializedRef.current) {
      return;
    }
    smartScriptInitializedRef.current = true;
    
    try {
      appsFlyerSmartScriptLib();
      console.log('[AppsFlyer] SmartScript loaded successfully on Subscription page');
    } catch (error) {
      console.error('[AppsFlyer] Failed to initialize SmartScript:', error);
    }
  }, []);

  return (
    <div className={clsx(stylesGlobal.container, styles.subscriptionPage)}>
      {/* Hero Video Section */}
      <HeroVideo 
        videoUrl={videoUrl}
      />

      {/* Trial Offer Section */}
      <TrialOffer />

      {/* Premium Benefits Section */}
      <PremiumBenefits />

      {/* User Review Slider Section */}
      <UserReviewSlider reviews={reviews} onReviewChange={handleReviewChange} />
      
      {/* Sticky Button at the bottom */}
      <TVStickyButton
        label="Start FREE trial"
        onClick={handleButtonClick}
        disabled={false}
        contextualText={contextualText}
      />
    </div>
  );
};

export default Subscription;